import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: 19,
        color: TEXT,
        margin: "32px 0 12px",
        letterSpacing: "-0.005em",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}

function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}
    >
      {children}
    </a>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        fontFamily: "var(--display)",
        fontSize: 20,
        fontWeight: 600,
        color: TEXT,
        lineHeight: 1.4,
        fontStyle: "normal",
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "40px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: TEAL_DIM,
        border: "1px solid rgba(78,205,196,0.25)",
      }}
    >
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>
        {children}
      </p>
      <a
        href={href}
        style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}
      >
        {label} &#8594;
      </a>
    </div>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        fontFamily: "var(--mono)",
        fontSize: "0.9em",
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

// ── HowTo schema step list ──
const howToSteps = [
  {
    name: "Lock your UTM convention before the first campaign ships",
    text: "Pick utm_source, utm_medium, utm_campaign, utm_content, and utm_term values that match your existing tagging across other paid channels. The canonical default for ChatGPT Ads is utm_source=chatgpt and utm_medium=cpc; under Google Analytics 4's default channel group that lands in Paid Other (a paid bucket), and a one-rule custom channel group gives ChatGPT Ads its own named channel.",
  },
  {
    name: "Construct the tagged destination URL",
    text: "Append the 5 parameters to your landing-page URL after a question mark, separated by ampersands. Use lowercase kebab-case values (utm_campaign=q2-launch, not Q2 Launch). Encode any space as %20. Validate the URL loads in a browser before pasting it into Ads Manager.",
  },
  {
    name: "Paste the tagged URL into ChatGPT Ads Manager Beta",
    text: "Open ads.openai.com and either set the destination URL on each ad creative to the tagged version, or add the parameters once under Landing page query parameters on the campaign, ad group or ad, where macros such as {ad_id} are filled in at delivery. OpenAI confirms that static tracking parameters persist on ad clicks and are forwarded to the landing page, so the UTM tags travel through the click intact.",
  },
  {
    name: "Verify the tags survive the click in your analytics tool",
    text: "After publishing the campaign, click your own ad in ChatGPT (or use the preview URL OpenAI provides), and confirm the destination URL still carries all 5 UTM parameters on arrival. Then open Google Analytics 4 (or HubSpot, Salesforce) and confirm the session shows the right source/medium/campaign values.",
  },
  {
    name: "Audit and align the convention quarterly",
    text: "Pull the last 90 days of utm_source and utm_medium values from GA4 and check for drift (chatgpt vs Chatgpt vs chatgpt-ads). Document the convention in a shared brief so anyone tagging URLs uses the same fields. Drift fragments your reporting into separate channels that should be one.",
  },
];

// ── FAQs (7 per house-style range 6-8) ──
const faqs = [
  {
    q: "Does ChatGPT Ads support UTM parameters on destination URLs?",
    a: "Yes. OpenAI&#39;s help center confirms: &quot;Advertisers can also track traffic from ChatGPT Ads by adding static tracking parameters (e.g. UTM parameters) to landing page URLs. These parameters persist on ad clicks and can be used in existing analytics tools to understand traffic from ChatGPT Ads.&quot; You add them to the destination URL of your ad in Ads Manager Beta the same way you would for Google Ads or LinkedIn Ads. They flow through to your landing page on click and get parsed by Google Analytics 4, HubSpot, Salesforce, and any other analytics tool that reads URL parameters.",
  },
  {
    q: "What is the recommended utm_source and utm_medium for ChatGPT Ads?",
    a: "The canonical default is utm_source=chatgpt and utm_medium=cpc. The chatgpt source matches the channel&#39;s natural name; the cpc medium aligns with Google Ads and Bing Ads paid-search convention and keeps the traffic in a paid bucket. Under GA4&#39;s default channel group it reports as Paid Other, because Paid Search additionally requires the source to be on Google&#39;s search-sites list and chatgpt is not on that list (verified against Google&#39;s published source-category list, August 2026). One custom channel group rule gives ChatGPT Ads its own named channel. If you have a custom channel group set up that splits AI ad platforms out separately, utm_medium=paid-ai or utm_medium=ai_ads are defensible alternatives. The important thing is locking one convention and never letting it drift.",
  },
  {
    q: "How are UTM parameters different from the OpenAI conversion pixel?",
    a: "Different mechanisms, complementary roles. UTM parameters travel on the URL from your ad to your landing page; analytics tools read them after the click and label the session. The OpenAI conversion pixel is a JavaScript SDK that runs on your site, captures OpenAI&#39;s privacy-preserving oppref identifier from ChatGPT ad clicks, and reports 10 standard conversion events back to OpenAI for in-platform optimization. UTMs are platform-agnostic (work in GA4, HubSpot, Salesforce, Mixpanel, Segment); the pixel reports back to OpenAI specifically. Most B2B advertisers run both.",
  },
  {
    q: "Will Google Analytics 4 auto-classify ChatGPT Ads as Paid Search?",
    a: "No, and this is the most repeated error in ChatGPT Ads analytics guides. GA4&#39;s Paid Search channel requires two conditions: the medium matches Google&#39;s paid regex (cpc qualifies) AND the source is on Google&#39;s search-sites list. chatgpt is not on that list (we verified Google&#39;s published source-category list in August 2026; it contains no AI assistant domains), so utm_source=chatgpt + utm_medium=cpc reports as Paid Other in default Acquisition reports. It is still cleanly a paid bucket, and one custom channel group rule keyed on session source matching chatgpt gives ChatGPT Ads its own named channel. Organic ChatGPT visits are separate: GA4&#39;s AI Assistants channel captures those automatically.",
  },
  {
    q: "Does utm_source=chatgpt.com on a session mean I got a ChatGPT Ads click?",
    a: "No. utm_source=chatgpt.com (with the .com) is a publisher-side parameter that ChatGPT auto-appends to outbound citation links on organic responses, so publishers can identify organic ChatGPT referrals. Advertiser-side ChatGPT Ads clicks carry whatever utm_source value you set yourself (typically utm_source=chatgpt without the .com). Both can show up in the same analytics view; the difference between them is whether you are looking at organic ChatGPT traffic (chatgpt.com) or paid ChatGPT Ads traffic (chatgpt or your custom value).",
  },
  {
    q: "Do UTM parameters work alongside the OpenAI oppref click identifier?",
    a: "Yes. The oppref parameter is OpenAI&#39;s own privacy-preserving click identifier appended to outbound ad clicks; it is read by the OpenAI conversion pixel SDK on the landing page and stored in a first-party __oppref cookie. UTM parameters live on the same URL alongside oppref and do not interfere with each other. Your landing page can have all of: utm_source=chatgpt, utm_medium=cpc, utm_campaign=q2-launch, utm_content=ad-variant-a, AND oppref=abc123. Each downstream tool reads the parameters it cares about.",
  },
  {
    q: "How do UTM parameters get into HubSpot, Salesforce, and the AI-Advisors stack?",
    a: "Each platform reads them differently. HubSpot captures UTMs as standard contact properties (hs_analytics_source, Original Source Drill-Down 1 / 2) when its tracking script loads on the landing page. Salesforce ingests UTMs through Web-to-Lead form mapping or Pardot/Marketing Cloud Account Engagement tracking; the destination field depends on which form layer captures the lead. AI-Advisors does not directly ingest UTM parameters; instead, the AI-Advisors ChatGPT Ads integration installs the OpenAI conversion pixel in your GTM container and the GA4 integration overlays your existing UTM-tagged GA4 sessions alongside AI bot traffic and citation data. UTMs flow into GA4 and your CRM through each platform&#39;s native tracking; AI-Advisors complements that with AI-side signals.",
  },
];

function Content() {
  return (
    <article>
      {/* ── Featured-snippet box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            UTM parameters work on ChatGPT Ads. OpenAI&#39;s help center confirms static tracking parameters persist on ad clicks and can be used in existing analytics tools. The canonical convention to use is utm_source=chatgpt and utm_medium=cpc, which <A href="/blog/how-to-track-chatgpt-ads-in-ga4">Google Analytics 4&#39;s default channel group</A> places in Paid Other (Paid Search requires a source on Google&#39;s search-sites list, which chatgpt is not; a one-rule custom channel group gives it its own channel). Add the tagged URL to your ad&#39;s destination field in Ads Manager Beta; the parameters travel through the click to your landing page, where GA4, HubSpot, Salesforce, and any other URL-parameter-reading analytics tool can attribute the session back to a specific ChatGPT Ads campaign.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 0" }}>
          <Li><Strong>OpenAI confirms support:</Strong> <A href="https://help.openai.com/en/articles/20001207-ads-in-chatgpt-the-basics">UTM parameters persist on ad clicks</A></Li>
          <Li><Strong>Canonical convention:</Strong> <Code>utm_source=chatgpt</Code>, <Code>utm_medium=cpc</Code>, <Code>utm_campaign=&#123;campaign&#125;</Code>, <Code>utm_content=&#123;ad-variant&#125;</Code>, <Code>utm_term=&#123;context-hint&#125;</Code></Li>
          <Li><Strong>UTM vs pixel:</Strong> UTMs are platform-agnostic post-click attribution; the OpenAI pixel is in-platform optimization. Most advertisers run both.</Li>
          <Li><Strong>Where UTMs land:</Strong> Google Analytics 4 (native), HubSpot (Original Source Drill-Down), Salesforce (Web-to-Lead/Pardot), AI-Advisors (via the GA4 overlay)</Li>
          <Li><Strong>Don&#39;t confuse:</Strong> advertiser-side <Code>utm_source=chatgpt</Code> (you set it) vs publisher-side <Code>utm_source=chatgpt.com</Code> (ChatGPT auto-appends on organic citations)</Li>
        </ul>
      </div>

      {/* ── Intro ── */}
      <P>
        ChatGPT Ads has a measurement gap. OpenAI&#39;s in-platform reporting shows impressions, clicks, spend, click-through rate, average cost-per-click, and conversions; the <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">conversion pixel</A> adds 10 standard event types. But neither tells your sales team which ChatGPT Ads campaign produced last week&#39;s pipeline. That answer lives in your analytics stack and your CRM, and the bridge that gets it there is the same one you have been using for 20 years: <A href="/glossary/utm-parameters">UTM parameters</A>.
      </P>
      <P>
        The good news is that ChatGPT Ads handles UTMs cleanly. <A href="https://help.openai.com/en/articles/20001207-ads-in-chatgpt-the-basics">OpenAI&#39;s help center documents it directly</A>: "Advertisers can also track traffic from ChatGPT Ads by adding static tracking parameters (e.g. UTM parameters) to landing page URLs. These parameters persist on ad clicks and can be used in existing analytics tools to understand traffic from ChatGPT Ads." You build the tagged URL the same way you do for Google Ads or LinkedIn Ads, paste it into the destination field in Ads Manager Beta, and the parameters travel through to your landing page on every click.
      </P>
      <P>
        This guide is the full operational walkthrough. It covers the disambiguation that trips up most teams (publisher-side vs advertiser-side UTMs), the pros and cons of UTM-based attribution versus the OpenAI conversion pixel, the canonical UTM convention to lock in across your team, how the data lands in Google Analytics 4, HubSpot, Salesforce, and AI-Advisors specifically, and the hygiene mistakes that quietly fragment attribution across half a dozen reporting buckets.
      </P>

      <PullQuote>
        UTM parameters are the only attribution layer that works in every analytics tool you already pay for. Use them on every ChatGPT Ads creative from day one.
      </PullQuote>

      {/* ── §1 quick answer ── */}
      <H2 id="quick-answer">The quick answer: UTM parameters work on ChatGPT Ads</H2>
      <P>
        Adding UTM parameters to ChatGPT Ads is the same three-line task it is on any paid channel that lets you set a custom destination URL. You construct the tagged URL, paste it into the ad creative&#39;s destination field in Ads Manager Beta, and verify the tags survive the click on the other side. OpenAI&#39;s help center confirms the parameters persist; the rest is downstream tool configuration.
      </P>
      <P>
        The full process is in the <Strong>Step-by-Step</Strong> sequence below. For now, the short version: tag every creative, lock your convention, and verify in <A href="/glossary/utm-parameters">analytics</A> before you scale spend.
      </P>

      {/* ── §2 disambiguation ── */}
      <H2 id="advertiser-vs-publisher">Two kinds of UTMs: advertiser-side vs publisher-side</H2>
      <P>
        The first thing that trips up teams searching for "UTM codes for ChatGPT" is that there are two completely different UTM contexts that share a name. They use the same syntax but serve different sides of the table.
      </P>

      <H3>Advertiser-side: UTMs you put on YOUR ad&#39;s destination URL</H3>
      <P>
        This is the context this guide covers. You are an advertiser running ChatGPT Ads. You construct a tagged destination URL with values you control: <Code>utm_source=chatgpt</Code>, <Code>utm_medium=cpc</Code>, <Code>utm_campaign=q2-launch</Code>. You paste it into Ads Manager Beta as the destination for your ad creative. When a user clicks the ad, the parameters travel from the ad to your landing page. Your analytics tools read them and label the session as coming from your ChatGPT Ads campaign.
      </P>

      <H3>Publisher-side: UTMs ChatGPT auto-appends to organic citation links</H3>
      <P>
        Starting in 2025, ChatGPT began auto-appending <Code>utm_source=chatgpt.com</Code> (note the .com) to outbound citation links in its organic responses. If your site gets cited in a ChatGPT answer and a user clicks through, the click arrives at your site with that parameter attached. You did not put it there; ChatGPT did. It is the AI engine&#39;s own auto-tagging on links it sends to publishers. It partially closes the <A href="/glossary/dark-ai-traffic">dark AI traffic</A> blind spot for sites that previously saw organic AI referrals classified as <Code>(direct) / (none)</Code> in their <A href="/glossary/ai-referral-traffic">AI referral traffic</A> reports.
      </P>

      <H3>How to tell them apart in your analytics</H3>
      <P>
        Both can coexist in the same analytics view. The naming convention does the work: <Code>utm_source=chatgpt.com</Code> (with the .com) is organic ChatGPT referral traffic; <Code>utm_source=chatgpt</Code> (without the .com, or whatever custom value you set) is ChatGPT Ads paid click traffic. If you want them merged into one "ChatGPT" channel for reporting, build a custom channel group keyed on a regex that matches both. If you want them separate, the default lexical split keeps them apart.
      </P>

      {/* ── §3 UTM vs pixel pros/cons ── */}
      <H2 id="utm-vs-pixel">UTM codes vs the OpenAI conversion pixel: pros and cons</H2>
      <P>
        Most B2B teams need both UTMs and the OpenAI conversion pixel running side by side. They are not competing mechanisms; they fill different gaps in the measurement stack.
      </P>

      <div className="blog-scroll-wrap" style={{ overflowX: "auto", margin: "16px 0 32px" }}>
        <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Dimension</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>UTM parameters</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>OpenAI conversion pixel</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["What it tracks", "Session source / medium / campaign on click", "10 standard events fired on the landing page"],
              ["Where the data lives", "Your analytics tool (GA4, HubSpot, Salesforce, etc.)", "OpenAI Ads Manager + your pixel events tab"],
              ["Setup time", "5 minutes per creative", "10 minutes via GTM; 2 minutes via AI-Advisors integration"],
              ["Cross-platform reporting", "Works in every analytics tool that reads URL parameters", "OpenAI side only (until OpenAI ships partner-data exports)"],
              ["Optimization side", "None; pure attribution layer", "Feeds OpenAI&apos;s bid-side optimization through the Conversions objective, selectable in Ads Manager as of August 2026"],
              ["Cost", "Free", "Free (install only); ad spend separate"],
              ["Ad blockers and ITP", "Unaffected (URL parameters are not blocked)", "Loses 5-15% of events to client-side blocking"],
              ["B2B revenue closure", "Closes loop via CRM; UTMs ride into HubSpot / Salesforce contact records", "Reports site-side events; revenue closure still happens in CRM"],
              ["Best for", "Cross-platform post-click attribution and CRM reconciliation", "OpenAI-side optimization signal and conversion volume reporting"],
            ].map(([dim, utm, pixel]) => (
              <tr key={dim} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "10px 12px", color: TEXT, fontWeight: 600 }}>{dim}</td>
                <td style={{ padding: "10px 12px", color: TEXT_DIM }} dangerouslySetInnerHTML={{ __html: utm }} />
                <td style={{ padding: "10px 12px", color: TEXT_DIM }} dangerouslySetInnerHTML={{ __html: pixel }} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <P>
        The decision is not "which one." It is "do I run both from day one, or stage them." For a B2B advertiser with a real CRM and a quarterly revenue close, the answer is both, from day one. UTMs close the post-click revenue loop through your existing analytics and CRM; the pixel feeds OpenAI&#39;s bid-side optimization through the <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">Conversions objective</A>, which is now selectable in Ads Manager with an Optimize-for conversion event. Skip the pixel and you are leaving in-platform optimization on the table. Skip UTMs and you have no way to attribute pipeline back to specific campaigns in the tools your finance team reports against.
      </P>

      <PullQuote>
        UTMs and the pixel are not competing. They report to different audiences. UTMs report to your CRM and finance team. The pixel reports to OpenAI&#39;s bid optimizer.
      </PullQuote>

      {/* ── §4 canonical UTM convention ── */}
      <H2 id="canonical-convention">The canonical UTM convention for ChatGPT Ads</H2>
      <P>
        Three different ChatGPT Ads UTM conventions are floating around the industry. The cleanest default for most B2B teams (and the one we recommend across the AI-Advisors playbook) is built around what Google Analytics 4 auto-classifies without custom channel groups.
      </P>

      <div className="blog-scroll-wrap" style={{ overflowX: "auto", margin: "16px 0 32px" }}>
        <table style={{ width: "100%", minWidth: 560, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Parameter</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Value</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Why</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["utm_source", "chatgpt", "Channel name; consistent across team and forward-compatible with utm_source=copilot or =perplexity when those ad platforms launch"],
              ["utm_medium", "cpc", "Keeps the traffic in a GA4 paid bucket (Paid Other by default; Paid Search needs a listed search source, which chatgpt is not)"],
              ["utm_campaign", "{campaign-slug}", "Kebab-case-lowercase campaign identifier matching your CRM campaign record"],
              ["utm_content", "{ad-variant}", "Specific creative or headline variant for A/B testing"],
              ["utm_term", "{context-hint-cluster}", "Repurposed from paid-search keyword field to hold the context-hint pattern or audience segment"],
            ].map(([param, val, why]) => (
              <tr key={param} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "10px 12px", color: TEXT, fontFamily: "var(--mono)", fontSize: 13 }}>{param}</td>
                <td style={{ padding: "10px 12px", color: TEXT, fontFamily: "var(--mono)", fontSize: 13 }}>{val}</td>
                <td style={{ padding: "10px 12px", color: TEXT_DIM }}>{why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <P>
        A full tagged destination URL looks like this (one-click copy below):
      </P>

      <CodeBlock lang="URL" syntax="text">{`https://yoursite.com/landing?utm_source=chatgpt&utm_medium=cpc&utm_campaign=q2-launch&utm_content=headline-a&utm_term=cluster-1`}</CodeBlock>

      <P>
        If your team has a custom GA4 channel group set up that splits AI ad platforms out separately (so ChatGPT Ads, Copilot Ads, and Gemini Ads get their own channel instead of pooling in Paid Other), <Code>utm_medium=paid-ai</Code> is a defensible alternative. It carries clearer semantic intent ("this is AI advertising specifically") at the cost of needing to define a custom channel group to surface in default reports.
      </P>

      {/* ── §5 GA4 ── */}
      <H2 id="set-once">Set your UTMs once, not on every ad</H2>
      <P>
        You do not have to paste a tagged URL into every ad. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001214-measure-results">Help Center</A> says Ads Manager supports dynamic URL parameters: on the Campaigns, Ad groups or Ads page, open the three-dot menu, choose Edit, and use <Strong>Landing page query parameters</Strong> to add parameters that are appended to every landing page URL. You can type fixed values or use supported macros, including <Code>&#123;campaign_id&#125;</Code>, <Code>&#123;ad_group_id&#125;</Code>, <Code>&#123;ad_id&#125;</Code> and <Code>&#123;ad_account_id&#125;</Code>, which Ads Manager fills in at delivery. Set at the campaign level, one line covers every ad in it:
      </P>
      <CodeBlock lang="Landing page query parameters" syntax="text">{`utm_source=chatgpt&utm_medium=cpc&utm_campaign=q4-pipeline&utm_content={ad_id}`}</CodeBlock>
      <P>
        Two rules decide which value wins. More specific settings take precedence: an ad&#39;s setting beats its ad group&#39;s, which beats the campaign&#39;s. And anything already written into the ad&#39;s own destination URL beats all of them, so if a creative&#39;s URL already carries <Code>utm_campaign</Code>, the campaign-level value will not replace it. Pick one home for each parameter: this field for the values that repeat, the URL only for exceptions.
      </P>
      <P>
        If you manage campaigns through OpenAI&#39;s Ads API, the same setting is <Code>landing_page_configuration.query_string_template</Code> on a campaign, ad group or ad, with account-level defaults beneath them, per OpenAI&#39;s <A href="https://developers.openai.com/ads/campaign-management">campaign management guide</A>. Its example also uses <Code>&#123;oppref&#125;</Code>, OpenAI&#39;s privacy-preserving attribution identifier. OpenAI&#39;s examples use <Code>utm_source=openai</Code> and <Code>utm_medium=paid</Code>; keep the convention above so ChatGPT Ads traffic lands in one consistent bucket. For product-feed ads, configured parameters override matching keys in the feed&#39;s product URLs, and OpenAI says accounts still on its Legacy feed ordering must switch by November 5, 2026.
      </P>

      <H2 id="ga4">Google Analytics 4: how UTMs from ChatGPT Ads show up</H2>
      <P>
        GA4 reads UTM parameters automatically with zero configuration. On any tagged session, GA4 writes <Code>session_source</Code>, <Code>session_medium</Code>, <Code>session_campaign</Code>, <Code>session_content</Code>, and <Code>session_term</Code> fields and labels the user&#39;s acquisition channel based on the default channel grouping (or your custom override).
      </P>
      <P>
        Three configuration tasks are worth doing on day one. <Strong>First</Strong>, build a custom Exploration filtered to <Code>session_source = chatgpt</Code> so you can isolate ChatGPT Ads performance at a glance. <Strong>Second</Strong>, define your conversion events explicitly in GA4 admin (form fills, demo requests, trial signups) so they show up in the Acquisition reports alongside source/medium. <Strong>Third</Strong>, if you want ChatGPT Ads broken out as its own named channel rather than pooled in Paid Other, build a custom channel group with a rule like <Code>session_source contains "chatgpt"</Code>.
      </P>
      <P>
        The <A href="/integrations/google-analytics-4">AI-Advisors GA4 integration</A> (Growth+ plans) pulls your tagged sessions back into AI-Advisors and overlays them alongside <A href="/glossary/ai-bot-traffic">AI bot traffic</A> and citation data, so you can see ChatGPT Ads click volume next to organic ChatGPT citation share on a single time-series chart. The UTMs themselves still live in GA4; AI-Advisors reads the tagged session data through the GA4 API.
      </P>

      {/* ── §6 HubSpot ── */}
      <H2 id="hubspot">HubSpot: how UTMs from ChatGPT Ads show up</H2>
      <P>
        HubSpot captures UTMs as standard contact properties when its tracking script loads on the landing page. The first-touch UTM values get written to the contact record on creation under three properties: <Code>hs_analytics_source</Code> (the channel bucket HubSpot maps utm_medium to), <Code>hs_analytics_source_drill_down_1</Code> (typically utm_source), and <Code>hs_analytics_source_drill_down_2</Code> (typically utm_campaign).
      </P>
      <P>
        Build a saved contact view filtered to <Code>Original Source Drill-Down 1 = chatgpt</Code> and pin it to your marketing dashboard. That view becomes the working list of ChatGPT Ads contacts; from there you can layer in lifecycle stage, deal value, and CRM source-tracking fields. HubSpot does not overwrite first-touch UTMs by default on subsequent visits, which means the attribution sticks to the contact record permanently (useful for B2B sales cycles that span weeks).
      </P>
      <P>
        One gotcha: the <Code>Original Source</Code> dropdown on a HubSpot contact has a set vocabulary (Paid Search, Email Marketing, Direct Traffic, etc.). It auto-maps from utm_medium, which is why utm_medium=cpc surfaces as "Paid Search" in the Original Source dropdown. If you use utm_medium=paid-ai or another non-standard value, the contact will land in "Other Campaigns" by default unless your team explicitly maps it.
      </P>

      {/* ── §7 Salesforce ── */}
      <H2 id="salesforce">Salesforce: how UTMs from ChatGPT Ads show up</H2>
      <P>
        Salesforce does not natively read URL parameters from a marketing site session. Getting UTMs into a Lead, Contact, or Opportunity record requires a form layer that captures them at the point of conversion. Three common patterns:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Web-to-Lead with hidden UTM fields.</Strong> The simplest setup. Add 5 hidden fields to your Web-to-Lead form (utm_source, utm_medium, utm_campaign, utm_content, utm_term), populate them via JavaScript that reads the URL query string on page load, and Salesforce stores them on the new Lead record. Map utm_source to the standard LeadSource field or a custom Campaign Source field.</Li>
        <Li><Strong>Pardot / Marketing Cloud Account Engagement tracking.</Strong> Pardot&#39;s tracking script captures UTMs natively and writes them to the Prospect record under standard Source fields. When the Prospect syncs to a Salesforce Lead or Contact, the UTM data rides along. Pardot also supports first-touch and last-touch attribution models out of the box.</Li>
        <Li><Strong>Marketo / HubSpot Forms with Salesforce sync.</Strong> If your form vendor captures UTMs and syncs to Salesforce (most do), the data lands on the matched Lead or Contact record through that sync. You configure the field mapping in the vendor&#39;s Salesforce integration settings.</Li>
      </ul>
      <P>
        Either way, the destination field in Salesforce is typically <Code>LeadSource</Code> (standard) or a custom <Code>Campaign_Source__c</Code> (recommended for cleaner reporting). Campaign Influence dashboards and revenue-attribution reports then pivot off that field to credit ChatGPT Ads with pipeline and closed-won revenue.
      </P>

      {/* ── §8 AI-Advisors role ── */}
      <H2 id="ai-advisors-role">AI-Advisors&#39;s role: complementary, not a UTM ingester</H2>
      <P>
        It is worth being honest about what AI-Advisors does and does not do with your UTM data. AI-Advisors is not a CRM or a standalone analytics platform; it does not directly ingest UTM parameters from your landing pages. Your UTMs flow into GA4, HubSpot, and Salesforce through their native tracking, exactly as they would whether or not you use AI-Advisors.
      </P>
      <P>
        Where AI-Advisors fits is on the upstream and downstream sides of UTM-based attribution.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Upstream:</Strong> the <A href="/integrations/chatgpt-ads">AI-Advisors ChatGPT Ads integration</A> installs the OpenAI conversion pixel in your GTM container in 2 minutes (Growth+ plans), so the pixel and your UTMs can run side by side on every landing page without a developer.</Li>
        <Li><Strong>Downstream:</Strong> the <A href="/integrations/google-analytics-4">AI-Advisors GA4 integration</A> (Growth+) overlays your UTM-tagged GA4 sessions alongside AI bot traffic and citation data, so you can see ChatGPT Ads click volume next to organic ChatGPT citation share on one time-series chart.</Li>
        <Li><Strong>The bigger frame:</Strong> AI-Advisors tracks the <A href="/glossary/ai-visibility-lift">AI Visibility Lift</A> across paid (ChatGPT Ads) and organic (citations from ChatGPT, Perplexity, Gemini, Claude, Grok). UTMs give you the click side of paid; AI bot tracking gives you the citation side of organic. Together they are the two halves of the AI marketing measurement stack.</Li>
      </ul>
      <P>
        On the CRM side, the <A href="/integrations/hubspot">AI-Advisors HubSpot integration</A> is live, and it reads exactly the fields this guide tells you to tag. It classifies contacts from the first-touch URL HubSpot already records: an exact ChatGPT Ads click id, AI UTM tagging, or an AI platform referrer. It then follows those contacts into their deals and writes the AI source back onto the contact record as native properties you can build lists, workflows, and reports on. A Salesforce connector is still on the roadmap; until it ships, the pattern there is to let Salesforce capture UTMs natively and pivot on those records inside its own reporting tools.
      </P>
      <ScreenshotFrame
        src="/screenshots/hubspot/chatgpt-ads-hubspot-command-center.png"
        title="HubSpot / ChatGPT Ads"
        alt="ChatGPT Ads command center inside HubSpot, showing an impressions to clicks to contacts to deals funnel with spend and ROI above a campaign table."
        caption="The full funnel on one screen: impressions to clicks to contacts to deals, with spend and ROI."
      />

      <InlineCTA href="/tools/google-ads-to-chatgpt-ads-converter" label="Run the free Converter">
        Turn your highest-spend Google Ads keywords into ChatGPT Ads context hints and ad copy, then add the canonical UTM convention above to each landing URL before you launch.
      </InlineCTA>

      {/* ── §9 UTM hygiene ── */}
      <H2 id="utm-hygiene">UTM hygiene: mistakes that quietly break attribution</H2>
      <P>
        UTM attribution fails silently. Nothing throws an error; the wrong session just lands in the wrong reporting bucket and you discover the drift three months later when the quarterly board deck does not tie. Five mistakes account for most of the failures.
      </P>

      <H3>Case-sensitivity drift</H3>
      <P>
        <Code>utm_source=Chatgpt</Code> and <Code>utm_source=chatgpt</Code> register as two different channels in Google Analytics 4 and most other analytics tools. The cure is a written convention everyone tags against (lowercase kebab-case is the safest default) and a URL builder that enforces it rather than ad-hoc typing into Ads Manager.
      </P>

      <H3>Inconsistent values across campaigns</H3>
      <P>
        One campaign tags with <Code>utm_source=chatgpt</Code>; the next uses <Code>chatgpt-ads</Code>; the third uses <Code>openai</Code>. Three campaigns report as three different channels and your "total ChatGPT Ads contribution" requires a manual union every time. Lock the convention before the first campaign ships and audit it quarterly.
      </P>

      <H3>Encoding issues on special characters</H3>
      <P>
        Spaces, ampersands, plus signs, and other special characters in UTM values break the URL or get parsed inconsistently across tools. Stick to URL-safe values (letters, numbers, hyphens, underscores). If a value has to contain a space, URL-encode it: <Code>q2%20launch</Code>, never <Code>q2 launch</Code>.
      </P>

      <H3>UTMs on internal site links</H3>
      <P>
        Tagging internal links inside your own site overwrites the original source attribution when the visitor clicks through. A visitor arrives from ChatGPT Ads with <Code>utm_source=chatgpt</Code>; they click an internal CTA tagged with <Code>utm_source=blog</Code>; the second UTM overwrites the first and ChatGPT Ads loses credit for the conversion. Reserve UTMs for external links only.
      </P>

      <H3>Forgetting to test the click before scaling</H3>
      <P>
        After publishing a campaign, click your own ad in ChatGPT (or use the preview URL OpenAI provides) and confirm the destination URL still carries all 5 UTM parameters on arrival. URL encoding issues, ad-network redirect chains, or stale form submissions can silently strip parameters. Verify before scaling spend; debugging a missing UTM after 30 days of spend is more painful than checking the URL once at launch.
      </P>

      <H3>Assuming every click lands on the page you set</H3>
      <P>
        Starting October 29, 2026, OpenAI turns on <A href="https://help.openai.com/en/articles/20001614-creative-enhancements-for-chatgpt-ads">creative enhancements</A> by default, and one of them can send a click to a different, more relevant page on your site than the destination URL you set. OpenAI does not say whether UTM parameters typed into that URL carry over to the new page. Check your analytics for ChatGPT Ads visits arriving on pages you never used as a destination, and confirm they still carry your tags. Where a campaign&#39;s attribution has to be exact, turn creative enhancements off for that campaign in Ads Manager. The same feature can rewrite headlines, so <Code>utm_content</Code> tells you which ad was clicked, not the exact wording the person saw.
      </P>

      <PullQuote>
        UTM attribution does not fail loudly. It quietly fragments your reporting into channels you never meant to create. Lock the convention; audit it quarterly.
      </PullQuote>

      {/* ── Schemas ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to add UTM codes to ChatGPT Ads",
            description:
              "Step-by-step process for adding UTM tracking parameters to ChatGPT Ads destination URLs so analytics tools (GA4, HubSpot, Salesforce) can attribute traffic and conversions back to specific ChatGPT Ads campaigns.",
            totalTime: "PT15M",
            step: howToSteps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
              url: `https://www.ai-advisors.ai/blog/how-to-add-utm-codes-to-chatgpt-ads#step-${i + 1}`,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q.replace(/&#39;/g, "'").replace(/&quot;/g, '"'),
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a.replace(/&#39;/g, "'").replace(/&quot;/g, '"'),
              },
            })),
          }),
        }}
      />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-optimize-landing-pages-for-chatgpt-ads">How to Optimize Landing Pages for ChatGPT Ads</A> (the landing-page side of the post-click conversion stack, paired with UTM tagging)</Li>
        <Li><A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">How to install the ChatGPT Ads Measurement Pixel via Google Tag Manager</A></Li>
        <Li><A href="/blog/chatgpt-ads-in-hubspot">How to Run ChatGPT Ads in HubSpot</A> (HubSpot auto-appends its own UTM parameters when auto-tracking is on, so decide which side owns the tagging before you launch)</Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/how-to-measure-roi-for-chatgpt-ads">How to Measure ROI for ChatGPT Ads: The 2026 ROAS Framework for B2B Marketers</A></Li>
        <Li><A href="/blog/how-to-report-on-chatgpt-ad-campaigns">How to Report on ChatGPT Ad Campaigns</A></Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking#conversational-conversion-stack">The Conversational Conversion Stack: How to Measure ChatGPT Ads</A> (the measurement model where UTM and CRM are Layer 2)</Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-add-utm-codes-to-chatgpt-ads",
  title: "How to Add UTM Codes to ChatGPT Ads",
  seoTitle: "How to Add UTM Codes to ChatGPT Ads (2026 Guide)",
  excerpt:
    "OpenAI confirms UTM parameters persist on ChatGPT Ads clicks. Here is the canonical 5-tag convention for utm_source, utm_medium, utm_campaign, utm_content, and utm_term, plus how UTM data lands in Google Analytics 4, HubSpot, Salesforce, and the AI-Advisors stack, with honest tradeoffs of UTM-based attribution versus the OpenAI conversion pixel.",
  metaDescription:
    "How to add UTM codes to ChatGPT Ads, the canonical convention to use, and how UTM data lands in GA4, HubSpot, Salesforce, and AI-Advisors.",
  date: "2026-05-13",
  lastUpdated: "2026-11-01",
  readTime: "11 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "UTM parameters", "UTM codes", "Attribution", "GA4", "HubSpot", "Salesforce", "HowTo"],
  Content,
};

export default post;
